import {
  Box,
  Button,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  Tooltip,
  Typography,
} from "@material-ui/core";
import Card from "@material-ui/core/Card";
import CardActions from "@material-ui/core/CardActions";
import CardContent from "@material-ui/core/CardContent";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import { LongText } from "components/system";
import DialogTitle from "components/system/dialog-title/DialogTitle";
import parse from "html-react-parser";
import MaterialTable from "material-table";
import { useHistory } from "react-router-dom";
import EnumDesignation from "utils/enum/EnumDesignation";
import LabelsContext from "../../../lang/LabelsContext";
import { dateView, getReviewStatus } from "../../../utils/Utils";
import EnumGroup from "../../../utils/enum/EnumGroup";
import EnumReviewStatus from "../../../utils/enum/EnumReviewStatus";
import CopyText from "../../system/copy-text/CopyText";
import HtmlTooltip from "../../system/html-tooltip/HtmlTooltip";
import { tableIcons } from "../../system/material-table-utils/MaterialTableUtils";
import OriginalFiles from "../../system/original-files/OriginalFiles";
import ReviewDialog from "../../system/review-dialog/ReviewDialog";
import MakeDecisionButton from "../make-decision-button/MakeDecisionButton";
import ArticleCardService, { ArticleCardServiceType } from "./ArticleCardService";
import useStyles from "./ArticleCardStyle";

interface ArticleCardType {
  data: any;
  index: number;
  rightComponent?: any;
  linkDisable?: boolean;
  showReviewsDialog?: boolean;
  animation?: boolean;
  showAssociateEditorDecision?: boolean;
}

const REVIEW_NOT_STATUS = [
  EnumReviewStatus.DRAFT,
  EnumReviewStatus.NEW_ARTICLE,
  EnumReviewStatus.REVIEWED,
  EnumReviewStatus.UNDER_REVIEW,
  EnumReviewStatus.REVIEWER_SELECTION,
  EnumReviewStatus.ASSOCIATE_EDITOR,
  EnumReviewStatus.RETURNED_TO_EDITOR,
  EnumReviewStatus.CORRECTION,
];

export default function ArticleCard({
  data,
  index,
  rightComponent,
  linkDisable,
  showReviewsDialog,
  animation,
  showAssociateEditorDecision,
}: ArticleCardType) {
  const {
    id,
    articleId,
    articleTitle,
    articleAuthors,
    type,
    status,
    files,
    uploadDate,
    lastUpdate,
    editorAssignements,
    suggestedReviewers,
    revision,
    languageService,
    uploadedBy,
  } = data;

  const classes = useStyles();
  const history = useHistory();
  const { Labels } = LabelsContext();

  const {
    figureDialogVisible,
    closeFigureDialog,
    openFigureDialog,
    figureList,
    selectedFigure,
    setSelectedFigure,
    originalDialogVisible,
    closeOriginalDialog,
    openOriginalDialog,
    reviewersDialogVisible,
    setReviewersDialogVisible,
    suggestReviewerDialogVisible,
    setSuggestReviewerDialogVisible,
    reviewers,
    authData,
    reviewsArticle,
    setReviewsArticle,
  }: ArticleCardServiceType = ArticleCardService({
    data,
  });

  const showArticleAuthors = (authors: any) => {
    return authors.map((x: any) => `${x.firstName} ${x.lastName}`).join(", ");
  };

  const showStatus = (status: string) => {
    return getReviewStatus(status, Labels);
  };

  const revisionTitle = revision ? `-R${revision}-` : " ";
  const title = articleId && articleTitle ? `#${articleId}${revisionTitle}${articleTitle}` : "#";
  const parseAuthor = articleAuthors ? showArticleAuthors(articleAuthors) : "";
  const parseStatus = status ? showStatus(status) : "";
  const assocEditor =
    editorAssignements?.length > 0
      ? editorAssignements[editorAssignements.length - 1].associateEditor.firstName +
        " " +
        editorAssignements[editorAssignements.length - 1].associateEditor.lastName
      : "None";
  const assocEditorDecision =
    editorAssignements?.length > 0
      ? editorAssignements[editorAssignements.length - 1].editorDecisions.length > 0
        ? getReviewStatus(
            editorAssignements[editorAssignements.length - 1].editorDecisions[
              editorAssignements[editorAssignements.length - 1].editorDecisions.length - 1
            ].decision,
            Labels
          )
        : "No decision"
      : "None";
  const assocEditorMessage =
    editorAssignements?.length > 0 &&
    editorAssignements[editorAssignements.length - 1].editorDecisions[
      editorAssignements[editorAssignements.length - 1].editorDecisions.length - 1
    ]
      ? editorAssignements[editorAssignements.length - 1].editorDecisions[
          editorAssignements[editorAssignements.length - 1].editorDecisions.length - 1
        ].decisionDescription
      : "None";

  const assocEditorComment =
    editorAssignements?.length > 0 &&
    editorAssignements[editorAssignements.length - 1].editorDecisions[
      editorAssignements[editorAssignements.length - 1].editorDecisions.length - 1
    ]
      ? editorAssignements[editorAssignements.length - 1].editorDecisions[
          editorAssignements[editorAssignements.length - 1].editorDecisions.length - 1
        ].decisionComment
      : "None";

  const parseSubmittedOn = uploadDate ? `${uploadDate[2]}/${uploadDate[1]}/${uploadDate[0]}` : "";
  const parseLastUpdate = lastUpdate ? `${lastUpdate[2]}/${lastUpdate[1]}/${lastUpdate[0]}` : "";

  const textDate = (text: string) => (
    <Typography className={classes.dateText} variant="subtitle2" color="textSecondary">
      {text}
    </Typography>
  );

  const buttonProps = {
    label: "accept decision",
    reviewStatus:
      (editorAssignements?.length > 0 &&
        editorAssignements[editorAssignements.length - 1].editorDecisions[
          editorAssignements[editorAssignements.length - 1].editorDecisions.length - 1
        ]?.decision) ??
      undefined,
    styleButton: { width: "170px", minHeight: "0px", fontWeight: "normal" },
  };

  const rightButtons = () => (
    <Grid container spacing={1}>
      {[EnumGroup.EDITOR, EnumGroup.ASSOCIATE_EDITOR, EnumGroup.ASSISTANT].includes(authData?.user?.group.group) && (
        <Grid item>
          <Button variant="contained" size="small" onClick={() => history.push(`/manageFiles/${id}`)}>
            Manage files
          </Button>
        </Grid>
      )}
      {[EnumGroup.ASSOCIATE_EDITOR, EnumGroup.EDITOR, EnumGroup.ASSISTANT].includes(authData?.user.group.group) && (
        <Grid item>
          <Button variant="contained" size="small" onClick={() => history.push(`/reviewerSelection/${id}`)}>
            {Labels.ACTION}
          </Button>
        </Grid>
      )}
    </Grid>
  );

  const InfoIcon = (title: string) => (
    <HtmlTooltip title={title}>
      <HelpOutlineIcon fontSize="small" className={classes.helpIcon} />
    </HtmlTooltip>
  );

  const filesLetterRebutalAll = files?.filter((f: any) => f.type === EnumDesignation.LETTER_OF_REBUTAL_PDF);
  const filesManuscriptFullAll = files?.filter((f: any) => f.type === EnumDesignation.FULL_ARTICLE);
  const filesManuscriptPDFAll = files?.filter((f: any) => f.type === EnumDesignation.FULL_MANUSCRIPT);

  const filesMarkedManuscriptAll = files?.filter((f: any) => f.type === EnumDesignation.MARKED_MANUSCRIPTS_PDF);
  const filesUnmarkedManuscriptAll = files?.filter((f: any) => f.type === EnumDesignation.UNMARKED_MANUSCRIPT);

  const filesLetterRebutal =
    filesLetterRebutalAll?.length > 0 ? filesLetterRebutalAll[filesLetterRebutalAll.length - 1] : null;
  const filesManuscriptFull =
    filesManuscriptFullAll?.length > 0 ? filesManuscriptFullAll[filesManuscriptFullAll.length - 1] : null;
  const filesManuscriptPDF =
    filesManuscriptPDFAll?.length > 0 ? filesManuscriptPDFAll[filesManuscriptPDFAll.length - 1] : null;
  const filesMarkedManuscript =
    filesMarkedManuscriptAll?.length > 0 ? filesMarkedManuscriptAll[filesMarkedManuscriptAll.length - 1] : null;

  const filesUnarkedManuscript =
    filesUnmarkedManuscriptAll?.length > 0 ? filesUnmarkedManuscriptAll[filesUnmarkedManuscriptAll.length - 1] : null;

  return (
    <>
      <Card
        key={index}
        className={[classes.root, animation && classes.rootAnimation].join(" ")}
        style={{ backgroundColor: index % 2 > 0 ? "rgba(56, 181, 74, 0.1)" : "transparent" }}
      >
        <CardContent>
          <Grid container justifyContent="space-between">
            <Grid item xs={12} sm={11}>
              <Grid container justifyContent="space-between">
                <Grid item xs={12} sm={11}>
                  {linkDisable ? (
                    <Box display="flex" alignItems="center">
                      <Typography variant="h6" align="left">
                        {title}
                      </Typography>
                      <CopyText text={title} />
                    </Box>
                  ) : (
                    <Grid>
                      <Link component="button" onClick={() => history.push(`/articleDetails/${id}`)}>
                        <Typography variant="h6" align="left">
                          {title}
                        </Typography>
                      </Link>
                      <CopyText text={title} />
                    </Grid>
                  )}
                </Grid>
              </Grid>

              <Typography className={classes.title} color="textSecondary" gutterBottom>
                {parseAuthor}
              </Typography>
              <Typography variant="body2">{`${Labels.TYPE}: ${type?.name}`}</Typography>
              <Typography variant="body2">{`${Labels.STATUS}: ${parseStatus}`}</Typography>
              {languageService && authData?.user.group.group === EnumGroup.ASSISTANT && (
                <Typography variant="body2">The authors want language correction service</Typography>
              )}
              <Typography variant="body2">{`${Labels.ASSOC_EDITOR}: ${assocEditor}`}</Typography>
              {showAssociateEditorDecision &&
                editorAssignements?.length > 0 &&
                editorAssignements[editorAssignements.length - 1].editorDecisions.length > 0 && (
                  <Grid style={{ display: "flex" }}>
                    <Card variant="elevation">
                      <CardContent>
                        <CardHeader
                          component={() => (
                            <Grid style={{ display: "flex" }}>
                              <Typography variant="h6" style={{ marginTop: 7 }}>
                                Associate editor decision
                              </Typography>
                              <CardActions disableSpacing>
                                <MakeDecisionButton
                                  article={data}
                                  buttonProps={buttonProps}
                                  editorDecision={true}
                                  message={assocEditorMessage}
                                />
                              </CardActions>
                            </Grid>
                          )}
                        />
                        <Typography variant="body2">{`${Labels.NAME}: ${assocEditor}`}</Typography>
                        <Typography
                          variant="body2"
                          style={{ marginTop: "5px", marginRight: "5px" }}
                        >{`${Labels.ASSOC_EDITOR_DECISION}: ${assocEditorDecision}`}</Typography>
                        <Card className={classes.card}>
                          <Grid className={classes.flexContainer}>
                            <Typography variant="subtitle1">Comment</Typography>
                            {InfoIcon("comment is visible to editor only")}
                          </Grid>
                          <Divider />
                          <div>{parse(assocEditorComment ?? "")}</div>
                        </Card>
                        <Card className={classes.card}>
                          <Grid className={classes.flexContainer}>
                            <Typography variant="subtitle1">Mail suggestion</Typography>
                            {InfoIcon("visible to all contributors")}
                          </Grid>
                          <Divider />
                          <div>{parse(assocEditorMessage ?? "")}</div>
                        </Card>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
            </Grid>
            <Grid item xs={12} sm={1}>
              <Grid container direction="column">
                <Grid item>{textDate(Labels.SUBMITTED_ON + ": ")}</Grid>
                <Grid item>{textDate(parseSubmittedOn)}</Grid>
                <Grid item>{textDate(Labels.LAST_UPDATE + ": ")}</Grid>
                <Grid item>{textDate(parseLastUpdate)}</Grid>
              </Grid>
            </Grid>
          </Grid>
        </CardContent>
        <CardActions>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item>
              <Grid container spacing={1}>
                {type?.name !== "Letters to the Editor" && files?.find((f: any) => f.type === "Abstract") && (
                  <Grid item>
                    <Link
                      href={files?.find((f: any) => f.type === "Abstract")?.location}
                      color="inherit"
                      component="a"
                      target="_blank"
                    >
                      <Button variant="contained" size="small">
                        {Labels.ABSTRACT}
                      </Button>
                    </Link>
                  </Grid>
                )}
                {files?.some((f: any) => f.type === "RebuttalLetterPDF") && (
                  <Grid item>
                    <Link href={filesLetterRebutal?.location} color="inherit" component="a" target="_blank">
                      <Tooltip title={"Letter of Rebuttal"} placement="bottom">
                        <Button variant="contained" size="small">
                          Letter of Rebuttal
                        </Button>
                      </Tooltip>
                    </Link>
                  </Grid>
                )}
                {files?.some((f: any) => f.type === EnumDesignation.FULL_ARTICLE) ? (
                  <Grid item>
                    <Link href={filesManuscriptFull?.location} color="inherit" component="a" target="_blank">
                      <Tooltip title={Labels.FULL_ARTICLE} placement="bottom">
                        <Button variant="contained" size="small">
                          {Labels.FULL}
                        </Button>
                      </Tooltip>
                    </Link>
                  </Grid>
                ) : files?.some((f: any) => f.type === EnumDesignation.FULL_MANUSCRIPT) ? (
                  <Grid item>
                    <Link href={filesManuscriptPDF?.location} color="inherit" component="a" target="_blank">
                      <Tooltip title="Full" placement="bottom">
                        <Button variant="contained" size="small">
                          {Labels.FULL}
                        </Button>
                      </Tooltip>
                    </Link>
                  </Grid>
                ) : (
                  <></>
                )}
                <Grid item>
                  <Tooltip title={Labels.ORIGINAL_FILES} placement="bottom">
                    <Button variant="contained" size="small" onClick={openOriginalDialog}>
                      {Labels.ORIGINAL}
                    </Button>
                  </Tooltip>
                </Grid>
                <Grid item>
                  {files?.find((f: any) => f.type === "MarkedManuscriptPDF") && (
                    <Link href={filesMarkedManuscript?.location} color="inherit" component="a" target="_blank">
                      <Tooltip title={Labels.COVER_LETTER} placement="bottom">
                        <Button variant="contained" size="small">
                          Marked Manuscript
                        </Button>
                      </Tooltip>
                    </Link>
                  )}
                </Grid>
                <Grid item>
                  {files?.find((f: any) => f.type === EnumDesignation.UNMARKED_MANUSCRIPT) && (
                    <Link href={filesUnarkedManuscript?.location} color="inherit" component="a" target="_blank">
                      <Tooltip title={Labels.COVER_LETTER} placement="bottom">
                        <Button variant="contained" size="small">
                          Unmarked Manuscript
                        </Button>
                      </Tooltip>
                    </Link>
                  )}
                </Grid>

                <Grid item>
                  {files?.find((f: any) => f.type === "CoverLetter") && (
                    <Link
                      href={files?.find((f: any) => f.type === "CoverLetter")?.location}
                      color="inherit"
                      component="a"
                      target="_blank"
                    >
                      <Tooltip title={Labels.COVER_LETTER} placement="bottom">
                        <Button variant="contained" size="small">
                          {Labels.COVER}
                        </Button>
                      </Tooltip>
                    </Link>
                  )}
                </Grid>

                {files?.some((f: any) => f.type === "Figure") && (
                  <Grid item>
                    <Button variant="contained" size="small" onClick={openFigureDialog}>
                      {Labels.FIGURE}
                    </Button>
                  </Grid>
                )}
                {showReviewsDialog &&
                  (![EnumGroup.GUEST, EnumGroup.AUTHOR, EnumGroup.REVIEWER].includes(authData?.user.group.group) ||
                    !REVIEW_NOT_STATUS.includes(status)) && (
                    <Grid item>
                      <Button size="small" variant="contained" onClick={() => setReviewsArticle(data)}>
                        {Labels.REVIEWS}
                      </Button>
                    </Grid>
                  )}

                {![EnumGroup.GUEST, EnumGroup.AUTHOR, EnumGroup.REVIEWER].includes(authData?.user.group.group) &&
                  uploadedBy?.email !== authData?.user?.email &&
                  reviewers?.length > 0 && (
                    <Grid item>
                      <Button variant="contained" size="small" onClick={() => setReviewersDialogVisible(true)}>
                        {Labels.REVIEWERS}
                      </Button>
                    </Grid>
                  )}
                {suggestedReviewers?.length > 0 && (
                  <Grid item>
                    <Button variant="contained" size="small" onClick={() => setSuggestReviewerDialogVisible(true)}>
                      Suggested reviewers
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item>{rightComponent ? rightComponent(data?.id) : rightButtons()}</Grid>
          </Grid>
        </CardActions>
      </Card>
      <Dialog
        maxWidth="xl"
        fullWidth={true}
        open={figureDialogVisible}
        onClose={closeFigureDialog}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle onClose={closeFigureDialog}>Figures</DialogTitle>
        <DialogContent>
          <Grid container spacing={3} direction="row">
            <Box display="flex" flexDirection="row">
              <Box display="flex" flexDirection="column" style={{ height: "80dvh", overflowY: "scroll" }}>
                {figureList &&
                  Object.keys(figureList)
                    .reverse()
                    .map((f: any) => (
                      <Grid key={f} direction="column">
                        <Typography variant="subtitle1" align="left">
                          Revision #{f}
                        </Typography>
                        <Divider />
                        {figureList[f].map((figure: any) => (
                          <Grid key={f}>
                            <Button
                              color="primary"
                              variant={selectedFigure.location === figure.location ? "contained" : "text"}
                              onClick={() => setSelectedFigure(figure)}
                            >
                              <LongText text={figure.filename} length={40} />
                            </Button>
                          </Grid>
                        ))}
                      </Grid>
                    ))}
              </Box>

              <Grid>
                {selectedFigure && (
                  <img src={selectedFigure.location} alt={selectedFigure.filename} className={classes.image} />
                )}
              </Grid>
            </Box>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeFigureDialog} color="primary">
            {Labels.CANCEL}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        maxWidth={"md"}
        fullWidth={true}
        open={reviewersDialogVisible}
        onClose={() => setReviewersDialogVisible(false)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle onClose={() => setReviewersDialogVisible(false)}>{Labels.REVIEWERS}</DialogTitle>
        <DialogContent>
          <MaterialTable
            icons={tableIcons}
            columns={[
              {
                title: Labels.NAME,
                render: (rowData: any) => <div>{rowData.reviewer.firstName + " " + rowData.reviewer.lastName}</div>,
              },
              { field: "status", title: Labels.STATUS },
              {
                field: "selectedOn",
                title: Labels.SELECTED_ON,
                render: (rowData: any) => <div>{dateView(rowData.selectedDate)}</div>,
              },
              {
                field: "mailedOn",
                title: Labels.MAILED_ON,
                render: (rowData: any) => <div>{dateView(rowData.mailedDate)}</div>,
              },
              {
                field: "decidedOn",
                title: Labels.DECIDED_ON,
                render: (rowData: any) => <div>{dateView(rowData.decidedDate)}</div>,
              },
              {
                field: "completedOn",
                title: Labels.COMPLETED_ON,
                render: (rowData: any) => <div>{dateView(rowData.completedDate)}</div>,
              },
            ]}
            title={""}
            data={reviewers}
            options={{
              actionsColumnIndex: -1,
              pageSize: 10,
            }}
            localization={{
              body: {
                emptyDataSourceMessage: <Typography>{Labels.NO_REVIEWERS_REVISION}</Typography>,
              },
            }}
            style={{ width: "100%" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewersDialogVisible(false)} color="primary">
            {Labels.CANCEL}
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        maxWidth={"md"}
        fullWidth={true}
        open={suggestReviewerDialogVisible}
        onClose={() => setSuggestReviewerDialogVisible(false)}
        aria-labelledby="form-dialog-title"
      >
        <DialogTitle onClose={() => setSuggestReviewerDialogVisible(false)}>Suggested reviewers</DialogTitle>
        <DialogContent>
          <MaterialTable
            icons={tableIcons}
            columns={[
              { field: "fullName", title: Labels.FULLNAME },
              { field: "email", title: Labels.EMAIL },
            ]}
            title={""}
            data={suggestedReviewers}
            options={{
              actionsColumnIndex: -1,
              pageSize: 10,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSuggestReviewerDialogVisible(false)} color="primary" variant="contained">
            {Labels.CANCEL}
          </Button>
        </DialogActions>
      </Dialog>
      <OriginalFiles files={files} visible={originalDialogVisible} close={closeOriginalDialog} />
      {showReviewsDialog && (
        <ReviewDialog
          articleId={reviewsArticle?.id}
          visible={id && reviewsArticle?.id && id === reviewsArticle.id}
          close={() => setReviewsArticle(undefined)}
        />
      )}
    </>
  );
}
